import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';
import { getAnnouncements, getMeCoach } from '@/lib/api-calls.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { validateRouteRole } from '@/lib/routing.js';
import { isCoach, isEdgeCoach } from '@airreading/common';
import { AnnouncementPlacement, TeachingPreference } from '@airreading/common/data';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AnnouncementContent } from '../teachers/announcement/-components/announcement-content.js';
import { myAvailabilityTabs, myAvailabilitySearchSchema } from './-components/my-availability-tabs.js';
import { WeeklyHoursList } from './-components/weekly-hours.js';

export const Route = createFileRoute('/_layout/my-availability/')({
  beforeLoad: validateRouteRole((user) => isCoach(user) && !isEdgeCoach(user)),
  validateSearch: myAvailabilitySearchSchema,
  component: Page,
});

function Page() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher availabilities. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <MySchedulePage />
    </InfoPageSuspenseBoundary>
  );
}

function MySchedulePage() {
  const { schedule } = Route.useSearch();
  const { data: profile } = useSuspenseQuery(getMeCoach());
  const substituteTeachingAvailability =
    profile.teachingPreferences?.includes(TeachingPreference.substitute_teaching) ?? false;
  const { data, isPending } = useQuery(
    getAnnouncements({
      pageIndex: 0,
      filters: {
        placements: [AnnouncementPlacement.teacher_my_schedule],
      },
    }),
  );

  const { announcements } = data || {};

  return (
    <div>
      <InfoPage
        headerText="My availability"
        data-testid="my-availability-header"
        tabs={myAvailabilityTabs(schedule, substituteTeachingAvailability)}
      >
        <div className="absolute top-4 right-10 grid grid-cols-1 gap-4">
          {!isPending &&
            announcements &&
            announcements.length > 0 &&
            announcements.map((announcement) => (
              <div key={announcement.id} className="xl:col-span-2">
                <AnnouncementContent announcement={announcement} />
              </div>
            ))}
        </div>
        <WeeklyHoursList />
      </InfoPage>
    </div>
  );
}
